import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MapPin, PlusCircle, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "./Marketplace";

export default function MarketplaceMyListings() {
  const { user } = useAuth();
  const [status, setStatus] = useState("all");
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const qs = status !== "all" ? `?status=${status}` : "";
        const r = await api.get(`/me/marketplace/listings${qs}`);
        setItems(r.data?.items || []);
      } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    })();
  }, [user?.id, status]);

  if (!user) return <div className="max-w-lg mx-auto py-20 px-6 text-center text-gray-600">Sign in.</div>;

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-8">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl">My listings</h1>
          <p className="mt-1 text-sm text-gray-600">Manage what you're selling on Servrevu Marketplace.</p>
        </div>
        <Link to="/marketplace/sell">
          <Button className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] text-white" data-testid="new-listing-button">
            <PlusCircle size={14} className="mr-2" /> New listing
          </Button>
        </Link>
      </div>

      <Tabs value={status} onValueChange={setStatus} className="mt-6">
        <TabsList className="rounded-sm">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="sold">Sold</TabsTrigger>
          <TabsTrigger value="hidden">Hidden</TabsTrigger>
        </TabsList>
      </Tabs>

      {items.length === 0 ? (
        <Card className="rounded-md p-10 mt-6 text-center text-gray-600" data-testid="my-empty">
          <ShoppingBag className="mx-auto mb-2 text-gray-400" size={28} />
          Nothing here yet — <Link to="/marketplace/sell" className="text-[#B31E1E] hover:underline">create your first listing</Link>.
        </Card>
      ) : (
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="my-listings-grid">
          {items.map((l) => (
            <Link key={l.id} to={`/marketplace/listings/${l.id}`}>
              <Card className="rounded-md overflow-hidden hover:border-[#B31E1E]/40 transition-colors">
                <div className="aspect-video bg-gray-100 relative">
                  {l.photos?.[0] ? (
                    <img src={l.photos[0]} alt={l.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300"><ShoppingBag size={28} /></div>
                  )}
                  {l.status !== "active" && (
                    <Badge className={`absolute top-2 right-2 rounded-sm ${l.status === "sold" ? "bg-red-600" : l.status === "hidden" ? "bg-gray-500" : "bg-amber-500"}`}>
                      {l.status}
                    </Badge>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-medium">{formatPrice(l.price_cents, l.currency)}</div>
                  <div className="text-sm text-gray-700 truncate">{l.title}</div>
                  <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-1">
                    <MapPin size={10} /> {l.city}{l.state ? `, ${l.state}` : ""} · {l.view_count || 0} views
                  </div>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
